// @flow

import * as React from 'react';
import cx from 'classnames';

import DropdownOption from '../dropdown-option';
import Dropdown from '../dropdown';
import type { Option, DropdownInputProps } from '../dropdown/typings';

import cs from './styles.pcss';

export type Props = {
  className?: string,
  placeholder?: string,
  value?: mixed,
  options: Array<Option>,
  searchable?: boolean,
  onChange?: (value?: string) => void,
  className: string,
};

export default function Select({
  searchable = false,
  onChange,
  options,
  placeholder,
  value,
  className,
  ...rest
}: Props): React.Node {
  const [keyword, setKeyword] = React.useState('');

  const getValue = (option, isShown) => {
    if (isShown && searchable) {
      return keyword;
    }

    const selected = option;
    return selected ? selected.label : '';
  };

  const handleChange = (newValue: mixed) => {
    const newSelectedOption = options.find((o) => o.value === newValue);
    if (onChange) {
      if (newSelectedOption) {
        onChange(newSelectedOption.value);
      } else {
        onChange();
      }
    }
  };

  const filterOptions = (): Array<Option> => {
    const trimmedKeyword = keyword ? keyword.trim() : '';
    if (trimmedKeyword !== '') {
      return options.filter((option) =>
        option.label.toLowerCase().includes(trimmedKeyword.toLowerCase())
      );
    }

    return options;
  };

  const renderEmptyDropdown = () => (
    <DropdownOption notFound>{null}</DropdownOption>
  );

  const renderToggleIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cs.toggleIcon}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8 13L13 4H3L8 13Z"
        fill="currentColor"
      />
    </svg>
  );

  return (
    <Dropdown
      {...rest}
      options={filterOptions()}
      onChange={handleChange}
      value={value}
      keyword={keyword ? keyword.trim() : ''}
      renderEmptyDropdown={renderEmptyDropdown}
    >
      {({
        toggleDropdown,
        handleKeyDown,
        option,
        isShown,
      }: DropdownInputProps) => (
        <>
          <input
            autoComplete="false"
            value={getValue(option, isShown)}
            placeholder={placeholder}
            className={cx(className, cs.select, {
              [cs.pointable]: !searchable,
            })}
            onKeyDown={handleKeyDown}
            onMouseDown={toggleDropdown}
            readOnly={!searchable}
            onChange={(e: SyntheticEvent<HTMLInputElement>) => {
              if (searchable) {
                const { value: newKeyword } = e.currentTarget;
                setKeyword(newKeyword);
                toggleDropdown(true);
              }
            }}
            onFocus={() => {
              toggleDropdown(true);
            }}
            onBlur={() => {
              setKeyword('');
              toggleDropdown(false);
            }}
          />
          {renderToggleIcon()}
        </>
      )}
    </Dropdown>
  );
}
